import type { ClientEntity } from '../entity/client.entity';
import type { CrossReferenceExternalArticleEntity } from '../entity/cross-reference-external-article.entity';
import type { ItemEntity } from '../entity/item.entity';

/**
 * PD-17504 — every other name one of our items goes by, as the item's
 * Cross-References tab and the Cross-References list under Items read them.
 *
 * Two tables hold these names, and both are shown as one list:
 * - `client_item` rows carrying a customer's own number (PD-17502);
 * - active `item_cross_reference` rows: competitor, maker and standard
 *   numbers that apply to every customer.
 * Recording one goes through `SaveItemCrossReferenceRequestDto`.
 */

/** The three kinds the screens show, as chips. */
export type ItemCrossReferenceRowKind =
	| 'client_item_number'
	| 'maker_or_competitor'
	| 'standard';

export const ITEM_CROSS_REFERENCE_ROW_KINDS: ReadonlyArray<ItemCrossReferenceRowKind> = [
	'client_item_number',
	'maker_or_competitor',
	'standard',
];

export const ITEM_CROSS_REFERENCE_ROW_KIND_LABELS: Record<ItemCrossReferenceRowKind, string> = {
	client_item_number: 'Client item number',
	maker_or_competitor: 'Maker or competitor',
	standard: 'Standard',
};

/**
 * Which table a row comes from. Removing a row depends on it: a customer's
 * number is cleared from its `client_item` row, an outside number is retired
 * (deactivated, never deleted).
 */
export type ItemCrossReferenceRowOrigin = 'client_item' | 'item_cross_reference';

/** One row of the tab or the list. */
export interface ItemCrossReferenceRowDto {
	/** Unique across both tables (`<origin>-<id>`), for the list's row id. */
	rowKey: string;
	origin: ItemCrossReferenceRowOrigin;
	/** The `client_item` or `item_cross_reference` row id. */
	recordId: number;
	itemId: ItemEntity['id'];
	itemDescription: ItemEntity['description'];
	/** Our item code: the tenant's code (`externalId2`), else `externalId`. */
	itemCode: string | null;
	/** The number exactly as they write it. */
	number: string;
	kind: ItemCrossReferenceRowKind;
	kindLabel: string;
	/** The customer whose number it is; null for a number every customer shares. */
	clientId: ClientEntity['id'] | null;
	clientName: ClientEntity['name'] | null;
	/** The client's name, or "Any customer". */
	whose: string;
	/** Where the number came from, in plain words ("Confirmed in a quote request"). */
	howWeKnow: string;
	/** The quote request whose confirmed line recorded it, if any. */
	quoteRequestId: number | null;
	/** When it was recorded (ISO), if known. */
	addedAt: string | null;
	/** Whether the caller holds the permission that removes this kind. */
	canRemove: boolean;
}

/**
 * An outside product (a reference product an external lookup researched)
 * whose number is linked to this item.
 */
export interface ItemOutsideProductDto {
	id: CrossReferenceExternalArticleEntity['id'];
	/** The maker the lookup reported, when it reported one. */
	maker: string | null;
	/** Their model or part number. */
	identifierValue: CrossReferenceExternalArticleEntity['identifierValue'];
	description: CrossReferenceExternalArticleEntity['description'];
	lookedUpAt: string;
	/**
	 * The attribute labels on which the outside product and our item state
	 * the same value ("Size", "Material"): why the two are equivalent.
	 */
	sameOn: Array<string>;
}

export interface ItemOutsideProductsResponseDto {
	success: boolean;
	data: Array<ItemOutsideProductDto>;
}
